import { Vector3, MathUtils, Vector2, TextureLoader } from "three";
import {
  CameraArguments,
  CameraFeature,
  CameraFile,
  CameraFileArguments,
  CameraImage,
  ImagePixel
} from ".";
import {Point} from "ol/geom";
import { CameraData } from "../../../redux/assets-slice";
import { getEulerInverse, SphericalPosition } from "../projections";
import { PanoramicImageMaterial } from "../rendering";
import { planeFromVectors, interpolate1D, roundDigit } from "../utilities";
import missingImagePath from "../../textures/misc/no-image-panoramic.png";

const loader = new TextureLoader();
const missingTexture = loader.load(missingImagePath);

class PanoramicFeature extends CameraFeature {
  constructor(opts) {
    super(opts);
  }
}

export class PanoramicImage extends CameraImage {
  constructor(...args: CameraArguments) {
    super(...args);
    this.missingTexture = missingTexture;
    this.material = new PanoramicImageMaterial();
  }

  getFeature() {
    const position = this.position.aerial;

    this.feature = new PanoramicFeature({
      geometry: new Point([position[0], position[1]]),
      camera: this
    });

    return this.feature;
  }

  pixelToDirection(pixel: ImagePixel) : Vector3 {
    if (!pixel) return null;

    let width = this.imageWidth;
    let height = this.imageHeight;

    let theta = -1*MathUtils.degToRad(360 * (pixel.u / width) - 180);
    let phi = MathUtils.degToRad(180 * (pixel.v / height));

    let imageDirection = new Vector3(
      Math.cos(theta) * Math.sin(phi),
      Math.sin(theta) * Math.sin(phi),
      Math.cos(phi)
    );

    let direction = imageDirection.clone()
      .applyEuler(this.rotation.euler);

    return direction;
  }

  directionToPixel(direction: Vector3) : ImagePixel {
    if (!direction) return null;

    const inverse = getEulerInverse(this.rotation.euler);
    const imageDirection = direction.clone()
      .applyEuler(inverse);

    let sphere = new SphericalPosition()
      .fromVectors(new Vector3(0,0,0), imageDirection);

    let width = this.imageWidth;
    let height = this.imageHeight;

    let u = (width*(-sphere.theta + 180)/360);
    let v = (height*(sphere.phi)/180);

    u = (u + width) % width;
    v = (v + height) % height;

    return {u, v};
  }

  directionsToPlanar(directions, planarSize=100) {
    // Convert directons to a flat plane based on average normal
    const plane = planeFromVectors(directions);
    let planar = directions.map(x => {
      let target = new Vector3();
      plane.projectPoint(x, target);
      return target;
    });

    let allX = planar.map(x => x.x);
    let allY = planar.map(x => x.y);

    let xMin = Math.min(...allX);
    let xMax = Math.max(...allX);
    let yMin = Math.min(...allY);
    let yMax = Math.max(...allY);

    planar = planar.map(point => {
      point.x = planarSize * (point.x - xMin) / (xMax-xMin);
      point.y = planarSize * (point.y - yMin) / (yMax-yMin);
      point.z = 0.0;
      return point;
    });

    return planar;
  }

  interpolatePixels(points, width, height) {
    let interpolated = [];

    // Convert pixels to angles
    let theta = points.map(x => MathUtils.degToRad((360 * x[0] / width) - 180));
    let phi = points.map(x => MathUtils.degToRad(180 * x[1] / height));

    // Convert angles to xyz
    let xyz = [];
    for (var i=0;i<points.length;i++) {
      xyz.push([
        Math.cos(theta[i]) * Math.sin(phi[i]),
        Math.sin(theta[i]) * Math.sin(phi[i]),
        Math.cos(phi[i])
      ]);
    }

    // Interpolate data along each axis
    let xyzTemp = [];
    let stepSize = 0.02;
    xyz.forEach((xyz1, index) => {
      let nextIndex = (index+1) % xyz.length;
      let xyz2 = xyz[nextIndex];
      let distance = new Vector3(...xyz1)
        .distanceTo(new Vector3(...xyz2));

      if (distance === 0) {
        return;
      }

      let steps = Math.ceil(distance/stepSize) + 1;
      let _x = interpolate1D(xyz1[0], xyz2[0], steps);
      let _y = interpolate1D(xyz1[1], xyz2[1], steps);
      let _z = interpolate1D(xyz1[2], xyz2[2], steps);

      let interpTemp = [];
      for (var i=0;i<steps;i++) {
        interpTemp.push([_x[i], _y[i], _z[i]]);
      }

      xyzTemp = [...xyzTemp, ...interpTemp];
    });
    xyz = [...xyzTemp];

    // Remove duplicates
    let xyzNoDups = [];
    xyz.forEach((current, index) => {
      if (index === 0) {
        xyzNoDups.push(current);
        return;
      }

      let previous = xyz[index - 1];
      let distance = new Vector3(...current)
        .distanceTo(new Vector3(...previous));

      if (distance !== 0) {
        xyzNoDups.push(current);
      };
    });
    xyz = [...xyzNoDups];

    // Convert xyz back to pixels
    xyz.forEach(current => {
      let r = new Vector2(...current).length();
      let _theta = MathUtils.radToDeg(Math.atan2(current[1], current[0]));
      let _phi = MathUtils.radToDeg(Math.atan2(r, current[2]));

      interpolated.push([
        roundDigit(width * ((_theta + 180) / 360), 4),
        roundDigit(height * (_phi / 180), 4)
      ]);
    });

    return interpolated;
  };
}

export class PanoramicCameraFile extends CameraFile {
  constructor(...args: CameraFileArguments) {
    super(...args);
  }

  getNewCamera(data: CameraData) {
    return new PanoramicImage(
      this,
      data,
      this.cameraHandler.cameraScene,
      this.cameraHandler.controls.object,
      this.cameraHandler.controls.mouseControls
    );
  }
}